import { GlobalMessages } from '@shared/localization/messages';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { useEstimationItemAssumptionQueryActions } from '@/api/hooks/estimationItemAssumptionQueryActions';
import { useEstimationModelQueryActions } from '@/api/hooks/estimationModelQueryActions';
import type { UpdateEstimationItemAssumptionRequest } from '@/api/schema/data-contracts';
import { Button } from '@/components/atoms/Button';
import { DropdownFormField } from '@/components/atoms/DropdownFormField';
import { Loader } from '@/components/atoms/Loader';
import { WysiwygFormTextField } from '@/components/atoms/WysiwygFormTextField';
import { ModalStateless } from '@/components/ModalStateless';
import { entityFieldSettings } from '@/constants/entityFieldSettings';
import { useMessages } from '@/hooks/messages';

import { assumptionEditationModalMessageDefinitions } from './messages';

interface AssumptionEditFormModel {
  assumptionCategoryId: string | null;
  description: string;
}

interface AssumptionEditationModalProps {
  estimationItemAssumptionId?: string;
  estimationModelId: string;
  estimationCultureName: string;
  open: boolean;
  onClose: () => void;
}

export const AssumptionEditationModal: FC<AssumptionEditationModalProps> = ({
  estimationItemAssumptionId,
  estimationModelId,
  estimationCultureName,
  open,
  onClose,
}) => {
  const globalMessages = useMessages(GlobalMessages);
  const messages = useMessages(assumptionEditationModalMessageDefinitions);

  const { useGetEstimationModel } = useEstimationModelQueryActions();
  const { data: estimationModel, isLoading: isLoadingEstimationModel } = useGetEstimationModel(
    estimationModelId,
    estimationCultureName,
  );

  const { useGetEstimationItemAssumption, useUpdateEstimationItemAssumption } =
    useEstimationItemAssumptionQueryActions();
  const { data: estimationItemAssumption, isLoading: isLoadingEstimationItemAssumption } =
    useGetEstimationItemAssumption(estimationItemAssumptionId);

  const assumptionMutation = useUpdateEstimationItemAssumption();

  const options = useMemo(
    () =>
      estimationModel?.assumptionCategories.map((category) => ({
        value: category.id,
        label: category.name,
      })) || [],
    [estimationModel?.assumptionCategories],
  );

  const formModelInitial = useMemo<AssumptionEditFormModel>(
    () => ({
      assumptionCategoryId: estimationItemAssumption?.estimationModelAssumptionCategoryId ?? null,
      description: estimationItemAssumption?.description ?? '',
    }),
    [estimationItemAssumption],
  );

  const { control, handleSubmit, reset } = useForm<AssumptionEditFormModel>({
    defaultValues: formModelInitial,
  });

  useEffect(() => {
    reset(formModelInitial);
  }, [estimationItemAssumptionId, reset, formModelInitial]);

  if (isLoadingEstimationModel || isLoadingEstimationItemAssumption) {
    return <Loader />;
  }

  if (!estimationModel) {
    throw new Error('Estimation model not found');
  }

  const onSubmit = (formModel: AssumptionEditFormModel) => {
    if (!estimationItemAssumption) {
      throw new Error('Estimation item assumption not found');
    }

    const assumptionToUpdate: UpdateEstimationItemAssumptionRequest = {
      description: formModel.description ?? '',
      isValid: estimationItemAssumption.isValid,
      estimationModelAssumptionCategoryId: formModel.assumptionCategoryId,
    };
    assumptionMutation.mutate({
      estimationItemAssumptionId: estimationItemAssumption.id,
      estimationItemAssumption: assumptionToUpdate,
    });
    onClose();
  };

  return (
    <ModalStateless
      title={messages.title()}
      open={open}
      onClose={onClose}
      buttons={
        <>
          <Button variant='secondary' outlined onClick={onClose}>
            {globalMessages.cancel()}
          </Button>
          <Button variant='primary' onClick={handleSubmit(onSubmit)}>
            {globalMessages.save()}
          </Button>
        </>
      }
    >
      <form className='space-y-5'>
        <div>
          <label htmlFor='assumptionCategoryId'>{messages.assumptionType()}</label>
          <DropdownFormField
            control={control}
            fieldPath='assumptionCategoryId'
            options={options}
            fullWidth
          />
        </div>
        <div>
          <label htmlFor='description'>{messages.description()}</label>
          <WysiwygFormTextField
            control={control}
            fieldPath='description'
            placeholder={messages.descriptionPlaceholder()}
            rules={{
              required: true,
              maxLength: entityFieldSettings.estimationItemAssumptionEntity.descriptionMaxLength,
            }}
          />
        </div>
      </form>
    </ModalStateless>
  );
};
